import { useEffect, useState, useRef } from 'react';
import { X, Shield, Copy, Check } from 'lucide-react';
import { useADStore } from '../../store/adStore';
import { ScrollArea } from '../../../../ui/common-ui/scroll-area';

const formatPropertyName = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const formatValue = (val: any): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (val instanceof Date) return val.toISOString();
  if (Array.isArray(val)) return val.join(', ');
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

const hasDisplayValue = (val: any): boolean => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') return val.trim().length > 0;
  if (typeof val === 'number' || typeof val === 'boolean') return true;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return true;
};

const kindConfig: Record<string, { bg: string; text: string; border: string }> = {
  User: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/20' },
  Group: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/20' },
  Computer: { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/20' },
  Domain: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/20' },
  OU: { bg: 'bg-cyan-500/10', text: 'text-cyan-500', border: 'border-cyan-500/20' },
  Container: { bg: 'bg-violet-500/10', text: 'text-violet-500', border: 'border-violet-500/20' },
  App: { bg: 'bg-orange-500/10', text: 'text-orange-500', border: 'border-orange-500/20' },
};

const defaultKind = { bg: 'bg-muted/50', text: 'text-muted-foreground', border: 'border-border/40' };

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-muted/60"
      title="Copy value"
    >
      {copied ? (
        <Check className="w-3 h-3 text-emerald-500" />
      ) : (
        <Copy className="w-3 h-3 text-muted-foreground" />
      )}
    </button>
  );
}

export function ADInspector() {
  const { selectedNode, selectNode } = useADStore();
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const prevNodeRef = useRef(selectedNode);

  // Handle open/close animations
  useEffect(() => {
    if (selectedNode && !prevNodeRef.current) {
      // Opening
      setVisible(true);
      requestAnimationFrame(() => setAnimating(true));
    } else if (!selectedNode && prevNodeRef.current) {
      // Closing
      setAnimating(false);
      const timer = setTimeout(() => setVisible(false), 300);
      prevNodeRef.current = selectedNode;
      return () => clearTimeout(timer);
    }
    prevNodeRef.current = selectedNode;
  }, [selectedNode]);

  const handleClose = () => {
    setAnimating(false);
    setTimeout(() => {
      selectNode(null);
      setVisible(false);
    }, 300);
  };

  if (!visible && !selectedNode) return null;

  const node = selectedNode || prevNodeRef.current;
  if (!node) return null;

  const kind = kindConfig[node.kind] || defaultKind;
  const properties = Object.entries(node.properties).filter(([, v]) => hasDisplayValue(v));
  const propertyCount = properties.length;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 z-40 transition-colors duration-300 ${
          animating ? 'bg-black/20' : 'bg-transparent pointer-events-none'
        }`}
        onClick={handleClose}
      />

      {/* Inspector panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[440px] z-50 flex flex-col transition-all duration-300 ease-out ${
          animating ? 'translate-x-0 shadow-[-12px_0_40px_rgba(0,0,0,0.15)]' : 'translate-x-full shadow-none'
        }`}
      >
        <div className="h-full flex flex-col bg-card border-l border-border/50 rounded-l-3xl overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-5 border-b border-border/30">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                {/* Avatar */}
                <div className={`w-12 h-12 rounded-2xl ${kind.bg} ${kind.border} border flex items-center justify-center flex-shrink-0`}>
                  <span className={`text-lg font-bold ${kind.text}`}>
                    {node.label?.charAt(0)?.toUpperCase() || 'N'}
                  </span>
                </div>

                <div className="min-w-0 flex-1">
                  <h2
                    className="text-base font-semibold text-foreground truncate"
                    title={node.label}
                  >
                    {node.label}
                  </h2>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-0.5 rounded-full font-medium ${kind.bg} ${kind.text} ${kind.border} border`}>
                      {node.kind}
                    </span>
                    <span className="text-[11px] text-muted-foreground/60">
                      {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={handleClose}
                className="p-2 -mt-1 -mr-1 rounded-xl hover:bg-muted/60 transition-all duration-150 group"
              >
                <X className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </button>
            </div>
          </div>

          {/* Tier Zero alert */}
          {node.isTierZero && (
            <div className="mx-5 mt-4 px-4 py-3 bg-red-500/8 border border-red-500/15 rounded-xl flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-red-500" />
              </div>
              <div>
                <span className="text-xs font-semibold text-red-500">Tier Zero</span>
                <p className="text-[11px] text-red-500/70 mt-0.5">High value target — critical asset</p>
              </div>
              <span className="ml-auto w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
            </div>
          )}

          {/* Properties list */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-5 space-y-2">
              {properties.map(([key, value], i) => (
                <div
                  key={key}
                  className="group flex flex-col gap-1 p-3.5 rounded-xl bg-muted/30 hover:bg-muted/50 border border-transparent hover:border-border/30 transition-all duration-150"
                  style={{ animationDelay: `${i * 20}ms` }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
                      {formatPropertyName(key)}
                    </span>
                    <CopyButton value={formatValue(value)} />
                  </div>
                  <span
                    className="text-sm text-foreground break-words font-mono leading-relaxed"
                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                  >
                    {formatValue(value)}
                  </span>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-border/30">
            <button
              onClick={handleClose}
              className="w-full py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150"
            >
              Close Inspector
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
